import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import AdvancedSearchFilters from '../components/bulletin/AdvancedSearchFilters';
import BulletinPageHeader from '../components/bulletin/BulletinPageHeader';
import BulletinStatisticsCards from '../components/bulletin/BulletinStatisticsCards';
import BulletinTable from '../components/bulletin/BulletinTable';
import ErrorDisplay from '../components/bulletin/ErrorDisplay';
import EmptyClasseState from '../components/bulletin/EmptyClasseState';
import LoadingState from '../components/bulletin/LoadingState';
import { useBulletinData } from '../hooks/useBulletinData';
import api from '../components/api';
import { toast } from 'react-toastify';

// Composants d'impression
import BulletinPrintZone from '../components/bulletin/BulletinPrintZone';
import PrintingIndicators from '../components/bulletin/PrintingIndicators';

// Services d'impression
import { printSingleBulletin, printAllBulletins } from '../services/printService';

const AllBulletinPage = () => {
  const navigate = useNavigate();
  const printRef = useRef();
  const [printingBulletin, setPrintingBulletin] = useState(null);
  const [etablissement, setEtablissement] = useState(null);
  const [tableauHonneur, setTableauHonneur] = useState(null);
  const [printingAll, setPrintingAll] = useState(false);
  
  // Utilisation du hook personnalisé pour gérer les données
  const {
    bulletins,
    classe,
    statistiques,
    pagination,
    loading,
    error,
    searchTerm,
    statusFilter,
    semestreFilter,
    anneeFilter,
    showFilters,
    semestres,
    annees,
    classeId,
    setSearchTerm,
    setStatusFilter,
    setSemestreFilter,
    setAnneeFilter,
    setShowFilters,
    fetchData,
    handlePageChange,
    handlePageSizeChange,
    clearFilters
  } = useBulletinData();

  // Gestionnaires d'actions
  const handleVoirBulletin = (bulletinId) => {
    navigate(`/bulletin/${bulletinId}`);
  };

  const handleDownload = (bulletinId) => {
    console.log('Téléchargement du bulletin:', bulletinId);
    // TODO: Implémenter le téléchargement
  };

  const handlePrint = async (bulletinId) => {
    try {
      setPrintingBulletin(bulletinId);
      
      const result = await printSingleBulletin(bulletinId);
      if (result.success) {
        setPrintingBulletin(result.bulletin);
        setEtablissement(result.etablissement);
        setTableauHonneur(result.tableauHonneur);
        
        setTimeout(() => {
          handlePrintBulletin();
        }, 100);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      toast.error('Erreur lors de l\'impression du bulletin');
      setPrintingBulletin(null);
    }
  };

  const handlePrintBulletin = () => {
    const printWindow = window.open('', 'PRINT');
    const content = printRef.current.innerHTML;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Bulletin de notes</title>
          <style>
            @page { 
              size: A4; 
              margin: 8mm; 
              padding: 0;
            }
            * { 
              margin: 0; 
              padding: 0; 
              box-sizing: border-box; 
            }
            body { 
              font-family: Arial, sans-serif; 
              font-size: 9pt; 
              line-height: 1.1; 
              color: #000; 
              background: white;
              max-height: 297mm;
              overflow: hidden;
            }
            table { 
              border-collapse: collapse; 
              width: 100%; 
              font-size: 7pt;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 2px 3px; 
              vertical-align: top; 
              font-size: 7pt;
            }
            th { 
              background-color: #f0f0f0; 
              font-weight: bold; 
              font-size: 7pt;
            }
            .no-border { border: none; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .bold { font-weight: bold; }
            .hidden { display: none !important; }
            .print\\:block { display: block !important; }
            .print\\:hidden { display: none !important; }
            
            .header-section { margin-bottom: 6px; }
            .student-info { margin-bottom: 4px; }
            .grades-table { margin-bottom: 4px; }
            .statistics-section { margin-bottom: 4px; }
            .footer-section { margin-top: 3px; }
            
            h1, h2, h3 { margin: 1px 0; }
            p { margin: 0.5px 0; }
            div { margin: 0.5px 0; }
            
            .compact-table th, .compact-table td {
              padding: 1px 2px;
              font-size: 6pt;
            }
            
            @media print {
              body { 
                margin: 0; 
                background: white;
                max-height: 297mm;
                overflow: hidden;
              }
              .print\\:block { display: block !important; }
              .print\\:hidden { display: none !important; }
            }
          </style>
        </head>
        <body>
          ${content}
          <script>
            window.onload = function() {
              setTimeout(() => { window.print(); window.close(); }, 100);
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    setTimeout(() => {
      setPrintingBulletin(null);
      setEtablissement(null);
      setTableauHonneur(null);
    }, 1000);
  };

  const handlePrintAll = async () => {
    if (!bulletins || bulletins.length === 0) {
      toast.warning('Aucun bulletin à imprimer');
      return;
    }

    setPrintingAll(true);
    try {
      await printAllBulletins(bulletins, classe);
      toast.success(`Impression de ${bulletins.length} bulletin(s) lancée`);
    } catch (error) {
      console.error('Erreur lors de l\'impression globale:', error);
      toast.error('Erreur lors de l\'impression globale');
    } finally {
      setPrintingAll(false);
    }
  };

  const handleEmail = (bulletinId) => {
    console.log('Envoi par email du bulletin:', bulletinId);
    // TODO: Implémenter l'envoi par email
  };

  // États de chargement et d'erreur
  if (!classeId) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <EmptyClasseState />
        </div>
      </Layout>
    );
  }

  if (loading && !classe) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <LoadingState />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* En-tête avec navigation */}
        <BulletinPageHeader
          classe={classe}
          statistiques={statistiques}
        />

        {/* Affichage des erreurs */}
        <ErrorDisplay error={error} onRetry={fetchData} />

        {/* Indicateurs d'impression */}
        <PrintingIndicators 
          printingBulletin={printingBulletin}
          printingAll={printingAll}
          bulletinsCount={bulletins?.length || 0}
        />

        {/* Statistiques de la classe */}
        {!error && (
          <BulletinStatisticsCards statistiques={statistiques} />
        )}

        {/* Section de recherche et actions unifiée */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
            {/* Barre de recherche principale */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher un étudiant par nom, prénom ou matricule..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Actions principales */}
            <div className="flex items-center space-x-3">
              {/* Bouton d'impression globale */}
              <button
                onClick={handlePrintAll}
                disabled={printingAll || !bulletins?.length}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md"
              >
                {printingAll ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Impression en cours...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Imprimer tous les bulletins
                  </>
                )}
              </button>

              {/* Bouton des filtres avancés */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 border ${
                  showFilters 
                    ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' 
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                </svg>
                <span>{showFilters ? 'Masquer' : 'Afficher'} les filtres</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Filtres avancés (conditionnels) */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <AdvancedSearchFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                semestreFilter={semestreFilter}
                setSemestreFilter={setSemestreFilter}
                anneeFilter={anneeFilter}
                setAnneeFilter={setAnneeFilter}
                showFilters={showFilters}
                setShowFilters={setShowFilters}
                onClearFilters={clearFilters}
                semestres={semestres}
                annees={annees}
              />
            </div>
          )}
        </div>

        {/* Liste des bulletins */}
        <BulletinTable
          bulletins={bulletins}
          pagination={pagination}
          loading={loading}
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          semestreFilter={semestreFilter}
          anneeFilter={anneeFilter}
          onVoirBulletin={handleVoirBulletin}
          onDownload={handleDownload}
          onPrint={handlePrint}
          onEmail={handleEmail}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />

        {/* Zone d'impression cachée */}
        <BulletinPrintZone 
          printingBulletin={printingBulletin}
          etablissement={etablissement}
          tableauHonneur={tableauHonneur}
          printRef={printRef}
        />
      </div>
    </Layout>
  );
};

export default AllBulletinPage;
